import { useMemo, useState } from 'react';
import { IconFilter, IconSearch } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { api } from '../api.js';
import { colorForTag } from './tagColor.js';

interface FacetSidebarProps {
  depotPath: string;
  // Active filter sets — sidebar reflects these and lets the user
  // toggle them. The parent owns the source of truth (the search
  // input), so toggling here calls onToggleClass / onToggleTag and
  // the parent rewrites the input string.
  activeClasses: ReadonlySet<string>;
  activeTags: ReadonlySet<string>;
  // D-5 budget + broken-ref facets.
  activeBudget: 'over' | 'large' | null;
  brokenRefsActive: boolean;
  onToggleClass: (name: string) => void;
  onToggleTag: (name: string) => void;
  onToggleBudget: (level: 'over' | 'large') => void;
  onToggleBrokenRefs: () => void;
}

// Map tag color names (Mantine palette legacy) to our Badge tone
// vocabulary. Mirrors the small mapper from AdminTags so the same
// color picks render consistently across both surfaces.
type Tone = 'neutral' | 'orange' | 'green' | 'red' | 'blue' | 'yellow';
function toneFromTagColor(name: string): Tone {
  switch (colorForTag(name)) {
    case 'green': return 'green';
    case 'yellow': return 'yellow';
    case 'red': return 'red';
    case 'blue':
    case 'cyan':
    case 'teal':
    case 'indigo':
      return 'blue';
    case 'orange':
    case 'pink':
    case 'grape':
    case 'violet':
    case 'lime':
      return 'orange';
    default: return 'neutral';
  }
}

/**
 * Left-rail facet filter for the depot view. Shows the top values
 * for Asset Class and Tag dimensions within the current scope,
 * each with a checkbox + count. Toggling a checkbox tells the
 * parent to rewrite the input string — single source of truth
 * stays the input.
 */
export function FacetSidebar({
  depotPath,
  activeClasses,
  activeTags,
  activeBudget,
  brokenRefsActive,
  onToggleClass,
  onToggleTag,
  onToggleBudget,
  onToggleBrokenRefs,
}: FacetSidebarProps) {
  const facets = useQuery({
    queryKey: ['depot.facets', depotPath],
    queryFn: () => api.depotFacets(depotPath),
    staleTime: 30_000,
  });

  const [classFilter, setClassFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');

  const classes = facets.data?.classes ?? [];
  const tags = facets.data?.tags ?? [];
  const budget = facets.data?.budget ?? { over: 0, large: 0 };
  const brokenRefs = facets.data?.brokenRefs ?? 0;
  const hasBudget = budget.over > 0 || budget.large > 0;

  // Filter values by sub-search. Active values stay visible even if
  // they don't match the search — losing sight of an applied filter
  // makes it impossible to remove.
  const visibleClasses = useMemo(() => {
    const needle = classFilter.trim().toLowerCase();
    if (!needle) return classes;
    return classes.filter(
      (c) => c.name.toLowerCase().includes(needle) || activeClasses.has(c.name),
    );
  }, [classes, classFilter, activeClasses]);
  const visibleTags = useMemo(() => {
    const needle = tagFilter.trim().toLowerCase();
    if (!needle) return tags;
    return tags.filter(
      (t) => t.name.toLowerCase().includes(needle) || activeTags.has(t.name),
    );
  }, [tags, tagFilter, activeTags]);

  const empty =
    !facets.isLoading &&
    classes.length === 0 &&
    tags.length === 0 &&
    !hasBudget &&
    brokenRefs === 0;

  return (
    <div className="flex w-60 shrink-0 flex-col gap-2">
      <div className="flex items-center gap-1.5 text-muted-foreground">
        <IconFilter size={12} />
        <span className="text-[10px] font-medium uppercase tracking-wider">
          Filters
        </span>
      </div>

      {facets.isLoading && <Spinner size="xs" />}
      {empty && (
        <p className="text-xs text-muted-foreground">
          No assets indexed under this scope yet. Tag or render a few to
          populate facets.
        </p>
      )}

      <div className="rounded-md border border-neutral-800 bg-card px-2">
        <Accordion type="multiple" defaultValue={['class', 'tag', 'budget', 'health']}>
          {(hasBudget || brokenRefs > 0) && (
            <AccordionItem value="health">
              <AccordionTrigger>Health &amp; budget</AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-1">
                  {budget.over > 0 && (
                    <FacetRow
                      label="Over budget"
                      labelClass="red"
                      count={budget.over}
                      checked={activeBudget === 'over'}
                      onToggle={() => onToggleBudget('over')}
                    />
                  )}
                  {budget.large > 0 && (
                    <FacetRow
                      label="Large (warn+)"
                      labelClass="yellow"
                      count={budget.large}
                      checked={activeBudget === 'large'}
                      onToggle={() => onToggleBudget('large')}
                    />
                  )}
                  {brokenRefs > 0 && (
                    <FacetRow
                      label="Broken references"
                      labelClass="red"
                      count={brokenRefs}
                      checked={brokenRefsActive}
                      onToggle={onToggleBrokenRefs}
                    />
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          )}
          {classes.length > 0 && (
            <AccordionItem value="class">
              <AccordionTrigger>Asset class</AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-1">
                  {classes.length > 8 && (
                    <FacetSearchInput
                      placeholder="Filter classes…"
                      value={classFilter}
                      onChange={setClassFilter}
                    />
                  )}
                  {visibleClasses.map((c) => (
                    <FacetRow
                      key={c.name}
                      label={c.name}
                      labelClass={null}
                      count={c.count}
                      checked={activeClasses.has(c.name)}
                      onToggle={() => onToggleClass(c.name)}
                    />
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          )}

          {tags.length > 0 && (
            <AccordionItem value="tag">
              <AccordionTrigger>Tag</AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-1">
                  {tags.length > 8 && (
                    <FacetSearchInput
                      placeholder="Filter tags…"
                      value={tagFilter}
                      onChange={setTagFilter}
                    />
                  )}
                  {visibleTags.map((t) => (
                    <FacetRow
                      key={t.name}
                      label={t.name}
                      labelClass={toneFromTagColor(t.name)}
                      count={t.count}
                      checked={activeTags.has(t.name)}
                      onToggle={() => onToggleTag(t.name)}
                    />
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          )}
        </Accordion>
      </div>
    </div>
  );
}

function FacetSearchInput({
  placeholder,
  value,
  onChange,
}: {
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="relative mb-1">
      <IconSearch
        size={11}
        className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground"
      />
      <Input
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.currentTarget.value)}
        className="pl-6"
      />
    </div>
  );
}

function FacetRow({
  label,
  labelClass,
  count,
  checked,
  onToggle,
}: {
  label: string;
  labelClass: Tone | null;
  count: number;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2 rounded-sm px-1 py-0.5 hover:bg-neutral-900">
      <Checkbox checked={checked} onCheckedChange={() => onToggle()} />
      <span className="flex min-w-0 flex-1 items-center justify-between gap-2">
        {labelClass ? (
          <Badge tone={labelClass} className="min-w-0 max-w-full truncate">
            {label}
          </Badge>
        ) : (
          <span className="min-w-0 truncate text-xs">{label}</span>
        )}
        <Badge tone="neutral">{count}</Badge>
      </span>
    </label>
  );
}
